import React, { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

export const FormRow = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${React.Children.count(children)}, 1fr)`, gap: 16 }}>
    {children}
  </div>
);

const labelStyle = {
  display: 'block', fontSize: 12.5, fontWeight: 600,
  color: '#374151', marginBottom: 7, letterSpacing: 0.15,
};

const containerBase = (focused, error, disabled) => ({
  display: 'flex', alignItems: 'center',
  border: `1.5px solid ${focused ? '#1e3a4c' : error ? '#ef4444' : '#d1d5db'}`,
  borderRadius: 4, background: disabled ? '#f9fafb' : '#fff',
  transition: 'border-color 0.18s, box-shadow 0.18s',
  boxShadow: focused ? '0 0 0 3px rgba(82,148,181,0.14)' : 'none',
});

const iconWrap = (focused) => ({
  padding: '0 0 0 14px', display: 'flex', alignItems: 'center',
  color: focused ? '#1e3a4c' : '#9ca3af', flexShrink: 0, transition: 'color 0.18s',
});

export const InputField = ({ label, icon, required, error, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={labelStyle}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        </label>
      )}
      <div style={containerBase(focused, error, props.disabled)}>
        {icon && <div style={iconWrap(focused)}>{React.cloneElement(icon, { size: 16 })}</div>}
        <input
          {...props}
          onFocus={e => { setFocused(true); props.onFocus?.(e); }}
          onBlur={e => { setFocused(false); props.onBlur?.(e); }}
          style={{
            flex: 1, height: 48, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 14, color: props.disabled ? '#9ca3af' : '#111827',
            padding: icon ? '0 16px 0 10px' : '0 16px',
            fontFamily: 'inherit',
          }}
        />
      </div>
      {error && <p style={{ fontSize: 11.5, color: '#ef4444', margin: '5px 0 0' }}>{error}</p>}
    </div>
  );
};

export const SelectField = ({ label, icon, required, children, error, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={labelStyle}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        </label>
      )}
      <div style={{ ...containerBase(focused, error, false), position: 'relative' }}>
        {icon && <div style={iconWrap(focused)}>{React.cloneElement(icon, { size: 16 })}</div>}
        <select
          {...props}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1, height: 48, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 14, color: '#111827',
            padding: icon ? '0 40px 0 10px' : '0 40px 0 16px',
            cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', fontFamily: 'inherit',
          }}
        >
          {children}
        </select>
        <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}>
          <FiChevronDown size={17} />
        </div>
      </div>
    </div>
  );
};

export const TextareaField = ({ label, icon, required, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={labelStyle}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        </label>
      )}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        border: `1.5px solid ${focused ? '#1e3a4c' : '#d1d5db'}`,
        borderRadius: 4, background: '#fff',
        transition: 'border-color 0.18s, box-shadow 0.18s',
        boxShadow: focused ? '0 0 0 3px rgba(82,148,181,0.14)' : 'none',
      }}>
        {icon && (
          <div style={{ paddingTop: 14, paddingLeft: 14, color: focused ? '#1e3a4c' : '#9ca3af', flexShrink: 0, transition: 'color 0.18s' }}>
            {React.cloneElement(icon, { size: 16 })}
          </div>
        )}
        <textarea
          {...props}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: 14, color: '#111827',
            padding: icon ? '13px 16px 13px 10px' : '13px 16px',
            resize: 'vertical', minHeight: 90, fontFamily: 'inherit', lineHeight: 1.5,
          }}
        />
      </div>
    </div>
  );
};

export const CheckField = ({ label, checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
      padding: '14px 18px',
      border: `1.5px solid ${checked ? '#1e3a4c' : '#d1d5db'}`,
      borderRadius: 4, background: checked ? '#f5f3ff' : '#fff',
      transition: 'all 0.18s', userSelect: 'none',
    }}
  >
    <div style={{
      width: 22, height: 22, borderRadius: 4, flexShrink: 0,
      border: checked ? 'none' : '2px solid #d1d5db',
      background: checked ? 'linear-gradient(135deg, #3d7a9c, #1e3a4c)' : '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.18s',
      boxShadow: checked ? '0 2px 6px rgba(82,148,181,0.36)' : 'none',
    }}>
      {checked && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800, lineHeight: 1 }}>✓</span>}
    </div>
    <span style={{ fontSize: 13.5, color: checked ? '#162e3c' : '#374151', fontWeight: checked ? 600 : 400, lineHeight: 1.4 }}>
      {label}
    </span>
  </div>
);

export const FileField = ({ label, required, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      {label && (
        <label style={labelStyle}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        </label>
      )}
      <div style={{
        border: `1.5px dashed ${focused ? '#1e3a4c' : '#d1d5db'}`,
        borderRadius: 4, background: '#fafafa',
        transition: 'border-color 0.18s', overflow: 'hidden',
      }}>
        <input
          type="file"
          {...props}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{ width: '100%', padding: '13px 16px', fontSize: 13.5, color: '#6b7280', cursor: 'pointer', background: 'transparent', border: 'none', outline: 'none', fontFamily: 'inherit' }}
        />
      </div>
    </div>
  );
};
